import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Divider } from '@material-ui/core';
import { useNavigate } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  root: {
    fontFamily: "'Inter', sans-serif",
    color: '#ffffff',
    padding: '26px 18px 0px',
    maxWidth: 980,
    margin: '0 auto',
    height: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    textAlign: 'left',

    [theme.breakpoints.down('sm')]: {
      padding: '10px 8px 0px',
    },
  },
  hero: {
    padding: '18px 18px 14px',
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.35)',
    background:
      'radial-gradient(circle at 18% 18%, rgba(77,121,255,0.35), rgba(10,14,30,0.96))',
    boxShadow: '0 0 24px rgba(77,121,255,0.14)',
    marginBottom: 18,

    [theme.breakpoints.down('sm')]: {
      padding: '10px 10px 10px',
      marginBottom: 10,
    },
  },
  titleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  title: {
    fontSize: '2.0rem',
    fontWeight: 900,
    letterSpacing: -0.4,
    lineHeight: 1.1,
    textShadow: '0 0 16px rgba(77, 121, 255, 0.55)',
  },
  subtitle: {
    marginTop: 8,
    color: '#cfd8ff',
    lineHeight: 1.6,
    fontSize: 13,
    maxWidth: 860,
    textAlign: 'left',
  },
  primaryButton: {
    backgroundColor: '#4d79ff',
    color: '#ffffff',
    textTransform: 'none',
    fontWeight: 800,
    borderRadius: 10,
    padding: '8px 12px',
    '&:hover': { backgroundColor: '#3e61cc' },
    [theme.breakpoints.down('sm')]: {
      fontSize: 11,
      padding: '6px 10px',
    },
  },
  section: {
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.22)',
    background: 'rgba(10, 14, 30, 0.82)',
    overflow: 'hidden',
    padding: 16,
    marginBottom: 14,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 900,
    letterSpacing: 0.35,
    textTransform: 'uppercase',
    color: '#e5e7ff',
    marginBottom: 10,
    textAlign: 'left',
  },
  body: {
    fontSize: 13,
    color: '#e8f5ff',
    lineHeight: 1.65,
    whiteSpace: 'pre-wrap',
    textAlign: 'left',
  },
  divider: {
    backgroundColor: 'rgba(255,255,255,0.10)',
    margin: '14px 0',
  },
  callout: {
    marginTop: 10,
    padding: 12,
    borderRadius: 12,
    backgroundColor: 'rgba(5, 8, 20, 0.88)',
    border: '1px solid rgba(158, 247, 197, 0.28)',
    color: '#e8f5ff',
    fontSize: 13,
    lineHeight: 1.65,
    textAlign: 'left',
  },
  note: {
    marginTop: 10,
    padding: 12,
    borderRadius: 12,
    backgroundColor: 'rgba(5, 8, 20, 0.88)',
    border: '1px solid rgba(255, 180, 80, 0.35)',
    color: '#e8f5ff',
    fontSize: 13,
    lineHeight: 1.65,
    textAlign: 'left',
  },
  step: {
    marginTop: 10,
    padding: 12,
    borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.10)',
    background: 'rgba(5, 8, 20, 0.62)',
  },
  stepTitle: {
    fontSize: 13,
    fontWeight: 900,
    color: '#ffffff',
    marginBottom: 6,
  },
}));

const workflows = [
  {
    title: 'Workflow A: Find what the agent is watching right now',
    problem: 'You want to know which tokens have active on-chain narrative events happening in real time — not what was interesting yesterday or last week, but what the agent is seeing right now.',
    steps: '1. Open /price.\n2. Set the Trend picker to "1H" (or "6H" for a slightly wider window).\n3. Click the Stories column header to sort descending.\n4. The tokens at the top of the table are the ones generating the most story activity in the last 1–6 hours. These are the tokens where something is happening on-chain — whale movements, surge events, cluster formations, liquidity changes — right now.\n5. Click any token to open its detail page and read the actual stories. The story narratives will tell you whether the activity is accumulation (potentially bullish), distribution or rug-pull mechanics (bearish), or structural anomaly (worth watching but not yet directional).',
    why: 'The 1H Stories count reflects on-chain events from the last sixty minutes, refreshed every 10 minutes via server-side ClickHouse queries. This is not delayed data. When you sort by Stories under 1H Trend, you are looking at a near-real-time radar sweep of narrative activity across the full 7,000+ token universe. No manual scanning, no social-media lag — just the agent\'s direct observations of what is happening on-chain.',
    who: 'Intraday traders scanning for setups. Alert builders looking for early narrative signals. Anyone who wants to see what the on-chain evidence shows before the price move becomes obvious on Twitter.',
  },
  {
    title: 'Workflow B: Find tokens with fresh AI conviction',
    problem: 'You do not want to sift through raw observations. You want the tokens where the agent has already done the synthesis work — where multiple converging stories triggered a structured directional brief.',
    steps: '1. Set the Trend picker to "24H" (the default, and the best window for thesis generation since convergence takes time).\n2. Click the Theses column header to sort descending.\n3. The tokens at the top have active theses generated in the last 24 hours. These are the tokens where the agent saw enough converging evidence to commit to a LONG, SHORT, or AVOID view.\n4. Click any token with a non-zero Theses count to open its detail page. Read the thesis direction, conviction score, entry signal, and invalidation condition. Cross-reference with the contributing stories to understand the evidence chain.',
    why: 'Theses are the highest-conviction output of the E3D agent. They require multiple independent on-chain signals to converge — a single story is not enough. When you sort by Theses descending, you are sorting by evidence density. The tokens at the top are the ones where the agent\'s rule engine fired on multiple high-weight convergence patterns: whale accumulation followed by cluster formation, multi-whale convergence on the same token, hot-links surge confirmed by a breakout event, and so on.',
    who: 'Swing traders looking for daily setups. Portfolio managers running a morning conviction scan. Anyone who wants the agent\'s structured judgment rather than its raw observations.',
  },
  {
    title: 'Workflow C: Find tokens with both stories and theses',
    problem: 'Stories alone can be noise — a single whale move or a thin-liquidity surge does not necessarily mean anything. Theses alone are powerful but you want to see whether the underlying narrative activity is still building. You want the tokens where both layers are firing.',
    steps: '1. Set the Trend picker to "24H."\n2. Sort by Theses descending.\n3. Scan the top results and look for rows where both the Stories column and the Theses column show non-zero, ideally high numbers.\n4. These are the tokens with the fullest AI coverage: raw on-chain activity (stories) confirmed and synthesized into structured conviction (theses). The more stories backing a thesis, the deeper the evidence chain you can verify.\n5. Click through to read both the thesis and the contributing stories. Check whether the story types align with the thesis direction — for example, a LONG thesis backed by WHALE accumulation + CLUSTER formation + SURGE confirmation is a different quality of signal than one backed by a single story type.',
    why: 'Stories and Theses are two stages of the same intelligence pipeline. Stories are observations; Theses are conclusions that require multiple observations to converge. A token with both high stories and a thesis has cleared two bars: the agent noticed a lot of activity, and then the agent decided that activity pointed somewhere specific. This is the highest-signal combination the screener can surface — the equivalent of finding both smoke and the fire alarm confirming the smoke is worth your attention.',
    who: 'Any trader or researcher who wants the strongest compound signal the screener can produce.',
  },
];

export default function PriceGuidePage() {
  const classes = useStyles();
  const navigate = useNavigate();

  return (
    <div className={classes.root}>
      <div className={classes.hero}>
        <div className={classes.titleRow}>
          <div>
            <div className={classes.title}>Price Page User Guide</div>
            <div className={classes.subtitle}>How to use the E3D token screener to find tokens with active AI-generated narratives and conviction — and why that gives you an edge other screeners can't.</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button className={classes.primaryButton} variant="contained" onClick={() => navigate('/price-reference')}>
              Reference
            </Button>
            <Button className={classes.primaryButton} variant="contained" onClick={() => navigate('/price-use-cases')}>
              Use Cases
            </Button>
            <Button className={classes.primaryButton} variant="contained" onClick={() => navigate('/stories')}>
              Stories
            </Button>
            <Button className={classes.primaryButton} variant="contained" onClick={() => navigate('/thesis')}>
              Theses
            </Button>
            <Button className={classes.primaryButton} variant="contained" onClick={() => navigate('/price')}>
              Back to Prices
            </Button>
          </div>
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>1) What makes this screener different</div>
        <div className={classes.body}>
          {'Open any crypto screener and you will see price, volume, and market cap. Those numbers tell you what happened. They do not tell you why it happened, whether anything interesting is happening beneath the surface, or whether anyone with analytical capability has formed a view about what comes next.\n\nE3D adds a layer that does not exist anywhere else: an AI agent that continuously monitors on-chain behavior across 7,000+ tokens and writes two kinds of output — Stories and Theses. Stories are the agent\'s field notes: raw observations about whale movements, cluster formations, liquidity shifts, anomalous trading patterns, and structural changes. Theses are the agent\'s structured conclusions: directional briefs (LONG, SHORT, or AVOID) generated when multiple independent stories converge on the same entity and the weight of evidence points in a clear direction.\n\nThe /price page puts both of these outputs into the table as sortable, filterable columns, right alongside price and volume. You can screen 7,000 tokens by narrative activity. You can sort by conviction. You can combine narrative signals with market-cap filters and sector tags. No other screener can do this, because no other screener has an AI agent generating structured intelligence about every token in its coverage set.\n\nThe result is a dashboard that shows you not just where the market is, but where the agent thinks the market is paying attention — and whether that attention has crystallized into a directional view.'}
        </div>
        <div className={classes.callout}>
          {'The Stories and Theses counts are interval-aware. When you select "24H Trend," you see how many stories and theses appeared in the last 24 hours — not all time. Switch to "1H" and the counts show only the last sixty minutes. This is the difference between looking at a weekly weather summary and checking the live Doppler radar. Both are useful, but they answer very different questions: "What has been happening?" vs. "What is happening right now?"'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>2) Reading the Stories column</div>
        <div className={classes.body}>
          {'The Stories column shows a single number: how many on-chain stories the agent generated for this token within the currently selected Trend interval. If the cell is blank, the agent had nothing to report about this token in this time window. If it shows a green number, something happened \u2014 and the agent thought it was worth writing about.\n\nWhat counts as a story: whale accumulations, cluster formations, liquidity drains, MEV sandwich attacks, surge events, wash-trade detections, exchange flows, smart-money movements, and roughly two dozen other on-chain patterns the agent monitors continuously. Each story is a structured record with a narrative, evidence references, and an evidence graph you can inspect.\n\nA higher story count does not automatically mean "bullish." A token with 12 stories in the last hour could be experiencing whale accumulation (potentially bullish), a rug-pull liquidity drain (definitely bearish), or a wash-trade loop (structurally suspicious). The number tells you the volume of on-chain narrative activity. The content of the stories tells you what that activity means. Always click through to read the actual stories before forming a view.\n\nClick the number \u2014 or click the token name in the Name column \u2014 to open the token\'s detail page, where every story is listed with its full narrative and evidence. From there you can drill into evidence graphs, inspect wallet-level transaction trails, and verify the agent\'s claims against raw on-chain data.\n\nThe fastest way to use this column: sort it descending. The tokens at the top are the ones the agent is writing the most about right now. Under a 1H Trend, that list is a real-time feed of on-chain narrative attention. Under a 24H Trend, it is a daily summary of where the agent focused its resources.'}
        </div>
        <div className={classes.note}>
          {'Stories are the sensor blips or field notes \u2014 raw signs that something happened on-chain. They tell you that something is moving, but they do not yet tell you whether it is a good setup or a warning sign.'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>3) Reading the Theses column</div>
        <div className={classes.body}>
          {'The Theses column is rarer and more decisive than Stories. Where Stories are the field notes \u2014 many observations, not all of them directional \u2014 Theses are the analyst\'s memo: a structured brief that says "I have seen enough to take a position."\n\nA thesis is generated only when the agent detects multiple converging stories for the same token that trigger its rule engine. The rules look for patterns like whale accumulation followed by cluster formation, or multiple whale addresses buying the same token independently, or a hot-links surge confirmed by a breakout event. When enough of these convergence rules fire \u2014 when the weight of evidence crosses a threshold \u2014 the agent writes a thesis.\n\nEach thesis contains a direction (LONG, SHORT, or AVOID), a conviction score, a narrative explanation of why the evidence points that way, a specific entry signal (the condition under which the thesis becomes actionable), an invalidation condition (the on-chain event that would kill the thesis), and a list of risk factors.\n\nA number in the Theses column means the agent committed to one or more directional views on this token within the current Trend interval. Even a "1" is significant. Unlike stories, which fire on individual events, a thesis requires convergence \u2014 multiple independent signals pointing in the same direction. The agent will not write a thesis on thin evidence.\n\nClick through to the token detail page to read the full thesis. Pay attention to the conviction score (higher is stronger), the entry signal (what to watch for), and especially the invalidation condition (the thing that would make the thesis wrong). A thesis is not a trade recommendation \u2014 it is a structured hypothesis with built-in exit criteria.'}
        </div>
        <div className={classes.note}>
          {'Theses are the analyst\'s memo or the coach\'s game plan. They are fewer than stories, but more decisive \u2014 each one represents the agent\'s highest-conviction output.'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>4) Three core workflows</div>
        <div className={classes.body}>
          {'The screener is most powerful when you use the Trend picker and column sorts together. Here are three workflows that cover the most common ways to extract signal from the table. Each one takes less than a minute and surfaces a different kind of edge.'}
        </div>
        {workflows.map((workflow) => (
          <div key={workflow.title} className={classes.step}>
            <div className={classes.stepTitle}>{workflow.title}</div>
            <div className={classes.note} style={{ marginTop: 0 }}>{'The Problem: ' + workflow.problem}</div>
            <div className={classes.body} style={{ marginTop: 10 }}>{workflow.steps}</div>
            <div className={classes.callout}>{'Why this works: ' + workflow.why}</div>
            <div className={classes.body} style={{ marginTop: 8, fontStyle: 'italic', color: '#aab4d0' }}>{'Who uses this: ' + workflow.who}</div>
          </div>
        ))}
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>5) The reset button</div>
        <div className={classes.body}>
          {'The X icon in the toolbar is the emergency reset. One click clears everything — search text, category filters, screener ranges, column visibility changes, sort order, and trend interval — and returns the table to its default state: 24H Trend, MCap descending, all tokens visible, all default columns showing.\n\nUse it when you have been deep in a filtered, sorted exploration and want to start a completely fresh scan. It is faster than manually undoing each change, and it guarantees you are back to a known baseline. Think of it as the "clear the board" button before a new investigation.'}
        </div>
        <Divider className={classes.divider} />
        <div className={classes.body}>
          {'If you remember only one thing, remember this: the /price page is not just a list of coins. It is a dashboard that shows you where the market is, where the agent thinks the market is paying attention, and whether that attention has crystallized into conviction — all controlled by a single time lens.'}
        </div>
      </div>
    </div>
  );
}
